import { parseBackendDate } from './parseBackendDate';
import i18n from '@/localization/i18n';

const pad2 = (n) => String(n).padStart(2, '0');

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

// i18n 的語言碼對到 Intl 認得的 BCP 47 tag。zh 指定 zh-TW：全站用繁體
// （DatePicker 載的也是 flatpickr 的 MandarinTraditional），不指定地區會拿到簡體用語
const INTL_LOCALES = { zh: 'zh-TW', ja: 'ja', en: 'en' };
const intlLocale = () => INTL_LOCALES[i18n.language] || INTL_LOCALES.zh;

// ── 後端字串 → 本地 Date ──────────────────────────────────────────────
//
// 後端統一回傳「無時區標記的 UTC 字串」YYYY-MM-DD HH:mm:ss：REST API 已對照 Postman 文件
// 逐欄確認，WebSocket 廣播（MessageSent／BellNotificationSent）也已實際抓封包確認同格式。
// 一律經 parseBackendDate 轉成 instant，絕不用 new Date(str) —— 那在 Safari 會被當本地時間
// 解析，時區直接差 8 小時。
//
// 回傳 null 的兩種情況，語意不同：
// - 傳入空值：後端的合法值（如 paid_at、expire_at 為 null 代表未付款／永久），呼叫端自行決定顯示什麼
// - 傳入非空但解析不出來：後端違反了格式約定，console.error 讓問題浮出來，不猜、不 fallback
const toLocalDate = (str) => {
  if (!str) return null;
  const ts = parseBackendDate(str);
  if (ts === null) {
    console.error('[formatDate] 無法解析的後端時間字串：', str);
    return null;
  }
  return new Date(ts);
};

const startOfLocalDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// 相差幾個「日曆日」（本地）。先各自歸零到當天 00:00 再相減，
// 直接用毫秒差除以 24h 的話，晚上 23:00 與隔天 01:00 只差 2 小時會被算成同一天
const calendarDayDiff = (a, b) => Math.round((startOfLocalDay(a) - startOfLocalDay(b)) / DAY_MS);

// ── 顯示用格式（一律本地時間）──────────────────────────────────────────
//
// 刻意不用 toLocaleString：zh-TW 會給出「2026/5/12 下午4:16」這種不補零又帶上下午的字串，
// 在表格（交易紀錄、Token 列表）裡每列寬度都不一樣，且與 DatePicker 的 24 小時制不一致。
// 全站固定 yyyy/MM/dd HH:mm，三種語系共用

// Date → yyyy/MM/dd HH:mm
export function formatDateTimeDisplay(date) {
  return `${date.getFullYear()}/${pad2(date.getMonth() + 1)}/${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

// Date → yyyy/MM/dd（模組內部用，對外請走吃後端字串的 formatDate）
function formatDateDisplay(date) {
  return `${date.getFullYear()}/${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}`;
}

// 後端字串 → yyyy/MM/dd HH:mm（本地）。帳務、稽核類資料用，使用者要看得到精確時間
export function formatDateTime(str) {
  const d = toLocalDate(str);
  return d ? formatDateTimeDisplay(d) : '';
}

// 後端字串 → yyyy/MM/dd（本地）。訂閱到期日、扣款日這類只需要到「日」的欄位用。
//
// ⚠️ 只吃「時間點」欄位（帶 HH:mm:ss 的 UTC 字串）。純日期欄位——生日 birthday（"1999-01-01"）、
// 月份 month（"2025-12"）——不要走這裡：那是「牆上日期」，沒有時刻語意，做時區換算會讓
// UTC 以西的使用者生日整整少一天。它們維持既有的本地欄位處理（toDateInputValue）。
// 傳進來也不會靜默出錯：parseBackendDate 的 regex 要求 HH:mm:ss，會走 console.error 分支。
// 注意不能用字串裁切（舊的 dateString.split(' ')[0]）：那是拿 UTC 的日期當本地日期，
// next_billing_date 這種帶時分的欄位在 UTC+8 早上 08:00 前會整整差一天
export function formatDate(str) {
  const d = toLocalDate(str);
  return d ? formatDateDisplay(d) : '';
}

// 給 <input type="date"> value 用：yyyy-MM-dd
export function toDateInputValue(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

// 送給後端用：yyyy-MM-dd HH:mm:ss（UTC，不含時區標記），對應 parseBackendDate 的反向轉換。
// 顯示層改本地後，送出端仍維持 UTC —— 後端的約定沒有變
export function toBackendDateTimeString(date) {
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())} ${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:${pad2(date.getUTCSeconds())}`;
}

// ── 相對時間 ──────────────────────────────────────────────────────────
//
// 用瀏覽器原生 Intl.RelativeTimeFormat（Safari 14+，專案支援範圍內），不引入日期套件。
// numeric: 'auto' 讓 -1 天輸出「昨天 / yesterday / 昨日」而不是「1 天前」，
// 0 秒輸出「現在 / now / 今」，三種語系都由 Intl 提供，不需要新增翻譯 key
const relativeFormatter = (() => {
  let cached = null;
  let cachedLocale = null;
  return () => {
    const locale = intlLocale();
    if (cachedLocale !== locale) {
      cached = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      cachedLocale = locale;
    }
    return cached;
  };
})();

// 後端字串 → 相對時間；超過 7 天改顯示絕對時間（相對值到那個尺度已經沒有可讀性）。
// 貼文、世界觀、留言、通知用。
//
// fallback 帶到「分」而不是只到「日」：這是該筆內容在畫面上唯一的時間資訊，
// 只給日期等於永久失去時分。聊天的日期分隔線與聊天室列表則相反，那裡的時分
// 由訊息自己的 HH:mm 承擔，分隔線只需要標日期（見 formatChatDayLabel／formatRoomTime）
export function formatRelativeTime(str) {
  const d = toLocalDate(str);
  if (!d) return '';

  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);
  if (abs >= WEEK_MS) return formatDateTimeDisplay(d);

  const rtf = relativeFormatter();
  // 一律用負值表示過去；diff 為正代表「已過去多久」，傳給 Intl 要反號
  if (abs < MINUTE_MS) return rtf.format(0, 'second');
  if (abs < HOUR_MS) return rtf.format(-Math.trunc(diff / MINUTE_MS), 'minute');
  if (abs < DAY_MS) return rtf.format(-Math.trunc(diff / HOUR_MS), 'hour');
  return rtf.format(-calendarDayDiff(new Date(), d), 'day');
}

// 相對時間是算出來的，卡片停在畫面上不動時文字不會自己變，需要定期重繪。
// 這個函式回傳「下一次該在幾毫秒後重算」，null 代表不必再重算。
//
// 刷新頻率跟著顯示尺度走，而不是固定每分鐘：
// - 一小時內：文字每分鐘會變（5 分鐘前 → 6 分鐘前），每 60 秒 tick
// - 一小時到七天：文字一小時才變一次，對齊到下一個整點再 tick，
//   一張卡片一小時只醒一次（首頁動輒幾十張 PostCard，每分鐘全部重繪並不划算）
// - 超過七天：已改顯示絕對日期，永遠不會再變，不排下一次
export function nextRelativeTickDelay(str) {
  const d = toLocalDate(str);
  if (!d) return null;
  const diff = Date.now() - d.getTime();
  const age = Math.abs(diff);
  if (age >= WEEK_MS) return null;
  if (age < HOUR_MS) return MINUTE_MS;
  // 過去與未來的方向相反：過去的 age 隨時間遞增，下一次文字變化落在下一個整點；
  // 未來（排程貼文）的 age 是遞減的，變化落在餘數歸零的那一刻。
  // 兩者都不能回傳 0，否則 effect 會同步重排成無窮迴圈
  return diff >= 0 ? HOUR_MS - (age % HOUR_MS) : Math.max(age % HOUR_MS, 1);
}

// 給 <time dateTime> 屬性用的 ISO 8601 字串（帶 Z）。畫面文字是本地時間，
// 但 dateTime 屬性依規範要放機器可讀的絕對時間點，輔助技術與爬蟲讀的是這個
export function toIsoString(str) {
  const d = toLocalDate(str);
  return d ? d.toISOString() : '';
}

// ── 聊天專用 ──────────────────────────────────────────────────────────
//
// 聊天訊息刻意不用相對時間：連續對話裡每則都寫「3 分鐘前」看不出節奏，
// 主流即時通訊一律是 HH:mm ＋ 跨日插日期分隔線

// 訊息氣泡旁的時間：HH:mm
export function formatChatTime(str) {
  const d = toLocalDate(str);
  return d ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}` : '';
}

// 訊息串的日期分隔線：今天 / 昨天 / yyyy/MM/dd
export function formatChatDayLabel(str) {
  const d = toLocalDate(str);
  if (!d) return '';
  const days = calendarDayDiff(new Date(), d);
  if (days === 0 || days === 1) return relativeFormatter().format(-days, 'day');
  return formatDateDisplay(d);
}

// 用來判斷兩則訊息之間要不要插日期分隔線
export function isSameLocalDay(strA, strB) {
  const a = toLocalDate(strA);
  const b = toLocalDate(strB);
  if (!a || !b) return false;
  return startOfLocalDay(a) === startOfLocalDay(b);
}

// 聊天室列表的最後訊息時間：今天顯示 HH:mm、昨天顯示「昨天 HH:mm」、更早顯示「日期 HH:mm」。
// （後端此欄位叫 created_at，但 API 文件註明語意是「最後一則訊息時間」）
//
// 三個分支都帶時分：這是聊天室列表上唯一的時間資訊，只給日期會讓使用者分不出
// 「今天稍早」與「那天深夜」。與訊息串的日期分隔線不同——分隔線旁邊每則訊息
// 都有自己的 HH:mm，那裡只需要標日期（見 formatChatDayLabel）
export function formatRoomTime(str) {
  const d = toLocalDate(str);
  if (!d) return '';
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const days = calendarDayDiff(new Date(), d);
  if (days === 0) return time;
  if (days === 1) return `${relativeFormatter().format(-1, 'day')} ${time}`;
  return `${formatDateDisplay(d)} ${time}`;
}
